import { AuthService } from './authService';
import { POrdersDto } from '../types/porders';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class POrdersService {
  
  /**
   * Henter alle porders for varemottak (OrderStatus < 60)
   */
  async getOrdersForReceiving(): Promise<POrdersDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/receiving`);
    
    if (!response.ok) {
      throw new Error(`Failed to get orders for receiving: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter alle porders
   */
  async getAllOrders(): Promise<POrdersDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders`);
    
    if (!response.ok) {
      throw new Error(`Failed to get all orders: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter porders basert på status
   */
  async getOrdersByStatus(status: number): Promise<POrdersDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/status/${status}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get orders by status ${status}: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter en spesifikk porder
   */
  async getOrderByOrderNo(orderNo: number): Promise<POrdersDto> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/${orderNo}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get order ${orderNo}: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter porders basert på leverandør
   */
  async getOrdersBySupplier(supplierNo: string): Promise<POrdersDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/supplier/${supplierNo}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get orders by supplier ${supplierNo}: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Søker etter porders basert på ERP-ordrenummer
   */
  async searchOrdersByERPOrderNo(erpOrderNo: string): Promise<POrdersDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/porders/search?erpOrderNo=${encodeURIComponent(erpOrderNo)}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to search orders by ERP order no: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter porders basert på datoperiode
   */
  async getOrdersByDateRange(fromDate: Date, toDate: Date): Promise<POrdersDto[]> {
    const fromDateStr = fromDate.toISOString().split('T')[0];
    const toDateStr = toDate.toISOString().split('T')[0];
    
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/porders/daterange?fromDate=${fromDateStr}&toDate=${toDateStr}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get orders by date range: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Oppdaterer status på en porder
   */
  async updateOrderStatus(orderNo: number, status: number): Promise<void> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/porders/${orderNo}/status`,
      { status }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to update order status: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Henter order-statistikk
   */
  async getOrderStatistics(): Promise<any> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/porders/statistics`);
    
    if (!response.ok) {
      throw new Error(`Failed to get order statistics: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Oppdaterer en ordre
   */
  async updateOrder(orderNo: number, order: Partial<POrdersDto>): Promise<POrdersDto> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/porders/${orderNo}`,
      order
    );
    
    if (!response.ok) {
      throw new Error(`Failed to update order ${orderNo}: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Formaterer dato til norsk format
   */
  formatDate(date: Date | string | null | undefined): string {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('no-NO');
  }
}

export const pOrdersService = new POrdersService();
